/**
 * Shared location utilities for header and PDP
 * Handles cookie read/write, Google Places address resolution
 */

import Cookies from 'js-cookie'

import { useCart } from '@/js/composables/cart'

const FALLBACK_POSTCODES = {
  NSW: '2000',
  ACT: '2000',
  VIC: '3000',
  QLD: '4000',
  SA: '5000',
  WA: '6000',
  TAS: '7000',
  NT: '0800'
}

/**
 * Read location from cookies
 * @returns {{ locationPreference: string, state: string, postalCode: string } | null}
 */
export function readLocationFromCookies () {
  const locationPref = Cookies.get('location_preference')
  const state = Cookies.get('state')
  const postalCode = Cookies.get('postal_code')

  if (!locationPref || !state || !postalCode) return null

  try {
    const decoded = decodeURIComponent(locationPref)
    if (!decoded || decoded === 'undefined' || decoded === 'null' || /undefined/i.test(decoded)) {
      return null
    }
    return { locationPreference: decoded, state, postalCode }
  } catch {
    return null
  }
}

/**
 * Resolve place ID to address and set cookies (same logic as header)
 * @param {string} placeId - Google Place ID
 * @param {Function} onSuccess - Callback with formatted address
 */
export function setLocationFromPlace (placeId, onSuccess) {
  const { updateCartAttributes } = useCart()
  const currentPostCode = Cookies.get('postal_code')
  const url = new URL(window.location.href)

  /* eslint-disable no-undef */
  if (typeof google === 'undefined' || !google.maps?.places?.PlacesService) {
    console.warn('Google Places not loaded')
    return
  }

  const placesService = new google.maps.places.PlacesService(document.createElement('div'))
  placesService.getDetails(
    { placeId, fields: ['address_components', 'formatted_address', 'name'] },
    (place, status) => {
      if (status !== google.maps.places.PlacesServiceStatus.OK) {
        console.error('PlacesService failed:', status)
        return
      }

      const components = place.address_components || []
      const addressBody = {}
      let stateCode = ''
      let updatedPostCode = ''

      for (const component of components) {
        if (component.types.includes('locality')) {
          addressBody.city = component.short_name
        }
        if (component.types.includes('administrative_area_level_1')) {
          addressBody.state = component.short_name
          stateCode = component.short_name
          Cookies.set('state', component.short_name, { expires: 365 })
        }
        if (component.types.includes('postal_code')) {
          addressBody.postCode = component.short_name
          updatedPostCode = component.short_name
          Cookies.set('postal_code', component.short_name, { expires: 365 })
          url.searchParams.set('postCode', component.short_name)
          window.history.replaceState({}, '', url)
          updateCartAttributes('postal_code', component.short_name)
        }
      }

      let formattedAddress
      if (addressBody.city && addressBody.state && addressBody.postCode) {
        formattedAddress = `${addressBody.city}, ${addressBody.state} ${addressBody.postCode}`
      } else if (stateCode) {
        Cookies.set('postal_code', FALLBACK_POSTCODES[stateCode] || '2000', { expires: 365 })
        url.searchParams.set('postCode', Cookies.get('postal_code'))
        window.history.replaceState({}, '', url)
        updateCartAttributes('postal_code', FALLBACK_POSTCODES[stateCode])
        formattedAddress = place.formatted_address
      } else {
        formattedAddress = place.formatted_address
      }

      Cookies.set('location_preference', formattedAddress, { expires: 365 })

      if (currentPostCode?.charAt(0) !== updatedPostCode?.charAt(0)) {
        window.location.reload()
      } else {
        window.dispatchEvent(new CustomEvent('location-updated', { detail: { locationPreference: formattedAddress } }))
        onSuccess?.(formattedAddress)
      }
    }
  )
  /* eslint-enable no-undef */
}
